/**
 * Super Admin Credits Dashboard — PHASE 2.
 *
 * Single page with tabs for:
 *  - Overview      (system-wide credit stats)
 *  - Users         (search + grant + set-unlimited + manual refund)
 *  - Pricing       (doctor + mom price-per-credit)
 *  - Feature Costs (4K / Video / Scan etc.)
 *  - Promo         (campaigns + code generation + CSV/PDF export + disable)
 *
 * Strict invariants:
 *  - Backend is the SOLE source of credit truth. Frontend never hardcodes
 *    costs, prices, or limits — every value comes from the listed endpoints.
 *  - Mom accounts ARE shown here (Super Admin scope, unlike Credit Admin).
 *  - All write actions go through adminCreditsApi or adminPromoApi.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout';
import { adminCreditsApi, adminPromoApi, downloadAuthenticated, ApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  Coins,
  Users as UsersIcon,
  Tags,
  Percent,
  Gift,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Download,
  FileText,
  Power,
  PowerOff,
  Infinity as InfinityIcon,
  Banknote,
  AlertCircle,
  ShoppingCart,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Overview
// ─────────────────────────────────────────────────────────────────────────────

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    adminCreditsApi
      .overview()
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Yükleniyor…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="overview-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Toplam Hesap" value={data.total_accounts} testid="stat-total-accounts" />
        <StatCard
          label="Aktif Rezervasyon"
          value={data.active_reservations}
          testid="stat-active-reservations"
        />
        <StatCard label="Ledger Kaydı" value={data.total_ledger_entries} testid="stat-ledger-count" />
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Mod ve Tip Bazında Dağılım</CardTitle>
          <button
            onClick={refresh}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            data-testid="overview-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Yenile
          </button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mod</TableHead>
                <TableHead>Kullanıcı tipi</TableHead>
                <TableHead className="text-right">Hesap</TableHead>
                <TableHead className="text-right">Toplam Bakiye</TableHead>
                <TableHead className="text-right">Rezerve</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(data.by_mode || {}).flatMap(([mode, info]) =>
                Object.entries(info.by_user_type || {}).map(([ut, row]) => (
                  <TableRow key={`${mode}-${ut}`} data-testid={`overview-row-${mode}-${ut}`}>
                    <TableCell>
                      <Badge variant="outline">{mode}</Badge>
                    </TableCell>
                    <TableCell>{ut}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.total_balance}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.total_reserved}</TableCell>
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="py-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-semibold tabular-nums">{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Users (search, detail dialog with grant + set-unlimited + refund)
// ─────────────────────────────────────────────────────────────────────────────

function UsersTab() {
  const [search, setSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // selected user account

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (search) params.search = search;
      if (userType) params.user_type = userType;
      const res = await adminCreditsApi.listUsers(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [search, userType]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4" data-testid="users-tab">
      <Card>
        <CardContent className="py-4 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Ara (e-posta veya isim)</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="örn. dr.smith"
                className="pl-9"
                data-testid="users-search-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Kullanıcı tipi</Label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="users-usertype-select"
            >
              <option value="">Tümü</option>
              <option value="doctor">Doktor</option>
              <option value="mom">Anne</option>
            </select>
          </div>
          <Button onClick={load} disabled={loading} data-testid="users-search-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ara'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-500">
            Toplam {total} kullanıcı
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-posta / İsim</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Mod</TableHead>
                <TableHead className="text-right">Bakiye</TableHead>
                <TableHead className="text-right">Rezerve</TableHead>
                <TableHead className="text-right">Free 4K</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`user-row-${row.user_id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900 text-sm">{row.email || '—'}</div>
                    <div className="text-xs text-slate-500">{row.name}</div>
                  </TableCell>
                  <TableCell>{row.user_type}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {row.credit_mode}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.balance}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.reserved_balance}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.free_4k_generation_total - (row.free_4k_generation_used ? 1 : 0)}/
                    {row.free_4k_generation_total}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActive(row)}
                      data-testid={`user-detail-btn-${row.user_id}`}
                    >
                      Yönet
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    Sonuç bulunamadı.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {active && (
        <UserDetailDialog
          userId={active.user_id}
          onClose={() => setActive(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

function UserDetailDialog({ userId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [refundResId, setRefundResId] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    adminCreditsApi
      .getUser(userId)
      .then(setDetail)
      .catch((e) => toast.error(e?.message || 'Yüklenemedi'));
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleGrant = async () => {
    const amt = parseInt(grantAmount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Pozitif bir miktar girin.');
      return;
    }
    setBusy(true);
    try {
      await adminCreditsApi.grant({
        user_id: userId,
        amount: amt,
        note: grantNote || undefined,
      });
      toast.success(`${amt} kredi verildi.`);
      setGrantAmount('');
      setGrantNote('');
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.message || 'Grant başarısız');
    } finally {
      setBusy(false);
    }
  };

  const handleSetUnlimited = async (enabled) => {
    setBusy(true);
    try {
      await adminCreditsApi.setUnlimited({
        user_id: userId,
        enabled,
        note: enabled ? 'Toggled ON via admin UI' : 'Toggled OFF via admin UI',
      });
      toast.success(enabled ? 'Sınırsız mod açıldı.' : 'Sınırsız mod kapatıldı.');
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.message || 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async () => {
    const id = refundResId.trim();
    if (!id) {
      toast.error('Reservation ID gerekli.');
      return;
    }
    setBusy(true);
    try {
      const res = await adminCreditsApi.manualRefund({
        reservation_id: id,
        reason: 'manual_admin_refund',
      });
      if (res?.success) {
        toast.success('Rezervasyon iade edildi.');
      } else {
        toast.warning('Rezervasyon zaten iade edilmiş veya onaylanmış.');
      }
      setRefundResId('');
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.message || 'İade başarısız');
    } finally {
      setBusy(false);
    }
  };

  const acc = detail?.account;
  const user = detail?.user;
  const isUnlimited = acc?.credit_mode === 'unlimited';

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle>Kullanıcı Kredi Yönetimi</DialogTitle>
          <DialogDescription>
            {user?.email || userId} — {acc?.user_type} / {acc?.credit_mode}
          </DialogDescription>
        </DialogHeader>

        {!detail ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-xs text-slate-500">Bakiye</div>
                <div className="text-xl font-semibold tabular-nums" data-testid="detail-balance">
                  {acc?.balance ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Rezerve</div>
                <div className="text-xl font-semibold tabular-nums">
                  {acc?.reserved_balance ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Free 4K</div>
                <div className="text-xl font-semibold tabular-nums">
                  {(acc?.free_4k_generation_total || 0) - (acc?.free_4k_generation_used ? 1 : 0)}/
                  {acc?.free_4k_generation_total || 0}
                </div>
              </div>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Kredi Ekle (sınırsız tutar)</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2 items-end">
                <div>
                  <Label className="text-xs">Miktar</Label>
                  <Input
                    type="number"
                    min={1}
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    className="w-28"
                    data-testid="detail-grant-amount"
                  />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs">Not</Label>
                  <Input
                    value={grantNote}
                    onChange={(e) => setGrantNote(e.target.value)}
                    placeholder="opsiyonel"
                    data-testid="detail-grant-note"
                  />
                </div>
                <Button onClick={handleGrant} disabled={busy} data-testid="detail-grant-btn">
                  Kredi Ver
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <InfinityIcon className="h-4 w-4 text-emerald-600" />
                  Sınırsız Mod
                </CardTitle>
              </CardHeader>
              <CardContent className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={busy || isUnlimited}
                  onClick={() => handleSetUnlimited(true)}
                  data-testid="detail-set-unlimited-on-btn"
                >
                  <Power className="h-4 w-4 mr-1" /> Aç
                </Button>
                <Button
                  variant="outline"
                  disabled={busy || !isUnlimited}
                  onClick={() => handleSetUnlimited(false)}
                  data-testid="detail-set-unlimited-off-btn"
                >
                  <PowerOff className="h-4 w-4 mr-1" /> Kapat
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-amber-600" />
                  Manuel Rezervasyon İadesi
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[260px]">
                  <Label className="text-xs">Reservation ID</Label>
                  <Input
                    value={refundResId}
                    onChange={(e) => setRefundResId(e.target.value)}
                    placeholder="aktif bir reservation_id"
                    data-testid="detail-refund-id-input"
                  />
                </div>
                <Button
                  onClick={handleRefund}
                  disabled={busy}
                  variant="outline"
                  data-testid="detail-refund-btn"
                >
                  İade Et
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Son Hareketler</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Tip</TableHead>
                      <TableHead>Özellik</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.history || []).slice(0, 10).map((h) => (
                      <TableRow key={h.id} data-testid={`detail-history-row-${h.id}`}>
                        <TableCell className="text-xs text-slate-500">
                          {(h.created_at || '').split('T')[0]}
                        </TableCell>
                        <TableCell>{h.type}</TableCell>
                        <TableCell>{h.feature_key || '—'}</TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${h.amount > 0 ? 'text-emerald-700' : h.amount < 0 ? 'text-red-700' : ''}`}
                        >
                          {h.amount > 0 ? '+' : ''}
                          {h.amount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="detail-close-btn">
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Pricing
// ─────────────────────────────────────────────────────────────────────────────

function PricingTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminCreditsApi
      .listPricing()
      .then((res) => {
        setItems(res.items || []);
        const edits = {};
        (res.items || []).forEach((p) => {
          edits[p.user_type] = String(p.price_per_credit);
        });
        setEdit(edits);
      })
      .catch((e) => toast.error(e?.message || 'Yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (userType) => {
    const v = parseFloat(edit[userType]);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Geçerli bir fiyat girin');
      return;
    }
    setSavingKey(userType);
    try {
      await adminCreditsApi.updatePricing(userType, v);
      toast.success('Fiyat güncellendi');
      load();
    } catch (e) {
      toast.error(e?.message || 'Güncelleme başarısız');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="pricing-tab">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Banknote className="h-4 w-4" /> Kredi Fiyatlandırması
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((p) => (
          <div
            key={p.id}
            className="flex flex-wrap items-end gap-3 border-b border-slate-100 last:border-0 py-2"
            data-testid={`pricing-row-${p.user_type}`}
          >
            <div className="min-w-[120px]">
              <Label className="text-xs capitalize">{p.user_type}</Label>
              <div className="text-xs text-slate-500">Şu anki: {p.price_per_credit} {p.currency}</div>
            </div>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={edit[p.user_type] || ''}
              onChange={(e) => setEdit((s) => ({ ...s, [p.user_type]: e.target.value }))}
              className="w-32"
              data-testid={`pricing-input-${p.user_type}`}
            />
            <Button
              size="sm"
              onClick={() => handleSave(p.user_type)}
              disabled={savingKey === p.user_type}
              data-testid={`pricing-save-btn-${p.user_type}`}
            >
              {savingKey === p.user_type ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Kaydet'
              )}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Feature Costs
// ─────────────────────────────────────────────────────────────────────────────

function FeatureCostsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminCreditsApi
      .listFeatureCosts()
      .then((res) => {
        setItems(res.items || []);
        const edits = {};
        (res.items || []).forEach((f) => {
          edits[f.feature_key] = String(f.credit_cost);
        });
        setEdit(edits);
      })
      .catch((e) => toast.error(e?.message || 'Yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (key) => {
    const v = parseInt(edit[key], 10);
    if (!Number.isFinite(v) || v < 0) {
      toast.error('Geçerli maliyet girin');
      return;
    }
    setSavingKey(key);
    try {
      await adminCreditsApi.updateFeatureCost(key, { credit_cost: v });
      toast.success('Maliyet güncellendi');
      load();
    } catch (e) {
      toast.error(e?.message || 'Güncelleme başarısız');
    } finally {
      setSavingKey(null);
    }
  };

  const toggleActive = async (item) => {
    setSavingKey(item.feature_key);
    try {
      await adminCreditsApi.updateFeatureCost(item.feature_key, {
        credit_cost: item.credit_cost,
        is_active: !item.is_active,
      });
      toast.success('Durum güncellendi');
      load();
    } catch (e) {
      toast.error(e?.message || 'Güncelleme başarısız');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="feature-costs-tab">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Tags className="h-4 w-4" /> Özellik Maliyetleri
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Anahtar</TableHead>
              <TableHead>Görünen ad</TableHead>
              <TableHead>Tip</TableHead>
              <TableHead>Maliyet</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((f) => (
              <TableRow key={f.id} data-testid={`feature-cost-row-${f.feature_key}`}>
                <TableCell className="font-mono text-xs">{f.feature_key}</TableCell>
                <TableCell>{f.display_name}</TableCell>
                <TableCell className="capitalize">{f.user_type_allowed}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    min={0}
                    value={edit[f.feature_key] || ''}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, [f.feature_key]: e.target.value }))
                    }
                    className="w-24"
                    data-testid={`feature-cost-input-${f.feature_key}`}
                  />
                </TableCell>
                <TableCell>
                  <Badge variant={f.is_active ? 'default' : 'outline'}>
                    {f.is_active ? 'aktif' : 'pasif'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => save(f.feature_key)}
                      disabled={savingKey === f.feature_key}
                      data-testid={`feature-cost-save-btn-${f.feature_key}`}
                    >
                      Kaydet
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActive(f)}
                      disabled={savingKey === f.feature_key}
                      data-testid={`feature-cost-toggle-btn-${f.feature_key}`}
                    >
                      {f.is_active ? 'Devre dışı bırak' : 'Etkinleştir'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Promo (campaigns + codes + export)
// ─────────────────────────────────────────────────────────────────────────────

function PromoTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminPromoApi
      .listCampaigns()
      .then((res) => setCampaigns(res.items || []))
      .catch((e) => toast.error(e?.message || 'Yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3" data-testid="promo-tab">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="promo-create-campaign-btn">
          <Plus className="h-4 w-4 mr-1" /> Yeni Kampanya
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Oluşturuldu</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Yükleniyor…
                  </TableCell>
                </TableRow>
              )}
              {!loading && !campaigns.length && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                    Henüz kampanya yok.
                  </TableCell>
                </TableRow>
              )}
              {campaigns.map((c) => (
                <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.campaign_type}</TableCell>
                  <TableCell>
                    <Badge variant={c.is_active ? 'default' : 'outline'}>
                      {c.is_active ? 'aktif' : 'pasif'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {(c.created_at || '').split('T')[0]}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActive(c)}
                      data-testid={`campaign-detail-btn-${c.id}`}
                    >
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {creating && (
        <CreateCampaignDialog onClose={() => setCreating(false)} onCreated={load} />
      )}
      {active && (
        <CampaignDetailDialog
          campaign={active}
          onClose={() => setActive(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function CreateCampaignDialog({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('medicine_box');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) {
      toast.error('Kampanya adı en az 2 karakter olmalı');
      return;
    }
    setBusy(true);
    try {
      await adminPromoApi.createCampaign({
        name: name.trim(),
        campaign_type: type,
        description: description || undefined,
      });
      toast.success('Kampanya oluşturuldu');
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.message || 'Oluşturulamadı');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="create-campaign-dialog">
        <DialogHeader>
          <DialogTitle>Yeni Promo Kampanyası</DialogTitle>
          <DialogDescription>
            Promo kodlar bu kampanyaya bağlanır.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Ad</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="create-campaign-name"
            />
          </div>
          <div>
            <Label className="text-xs">Tip</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="create-campaign-type"
            >
              <option value="medicine_box">medicine_box</option>
              <option value="clinic_demo">clinic_demo</option>
              <option value="fair_event">fair_event</option>
              <option value="manual">manual</option>
              <option value="other">other</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Açıklama (opsiyonel)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="create-campaign-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={submit} disabled={busy} data-testid="create-campaign-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Oluştur'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignDetailDialog({ campaign, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // generate form
  const [count, setCount] = useState(1);
  const [type, setType] = useState('fixed_credits');
  const [value, setValue] = useState(1);
  const [featureKey, setFeatureKey] = useState('');
  const [userTypeAllowed, setUserTypeAllowed] = useState('both');
  const [maxRedemptions, setMaxRedemptions] = useState(1);
  const [prefix, setPrefix] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const reload = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        adminPromoApi.getCampaign(campaign.id),
        adminPromoApi.listCodes(campaign.id, { limit: 100 }),
      ]);
      setDetail(d);
      setCodes(c.items || []);
    } catch (e) {
      toast.error(e?.message || 'Yüklenemedi');
    }
  }, [campaign.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleGenerate = async () => {
    if (type === 'feature_specific_usage' && !featureKey.trim()) {
      toast.error('feature_specific_usage için feature_key zorunlu');
      return;
    }
    setBusy(true);
    try {
      const body = {
        count: parseInt(count, 10),
        type,
        value: parseInt(value, 10),
        user_type_allowed: userTypeAllowed,
        max_redemptions: parseInt(maxRedemptions, 10),
        prefix: prefix || undefined,
        feature_key: featureKey || undefined,
        expires_at: expiresAt || undefined,
      };
      const res = await adminPromoApi.generateCodes(campaign.id, body);
      toast.success(`${res.count} kod üretildi.`);
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.detail?.message_tr || e?.message || 'Üretilemedi');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async () => {
    setBusy(true);
    try {
      await adminPromoApi.setCampaignActive(campaign.id, !detail?.is_active);
      toast.success('Kampanya durumu güncellendi.');
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.message || 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const handleDisableCode = async (codeId) => {
    try {
      await adminPromoApi.disableCode(codeId);
      toast.success('Kod devre dışı bırakıldı.');
      reload();
    } catch (e) {
      toast.error(e?.message || 'İşlem başarısız');
    }
  };

  const handleExport = async (kind) => {
    setExporting(true);
    try {
      const url =
        kind === 'csv'
          ? adminPromoApi.exportCsvUrl(campaign.id)
          : adminPromoApi.exportPdfUrl(campaign.id);
      const filename = `promo-codes_${campaign.name.replace(/[^a-zA-Z0-9]+/g, '_')}_${campaign.id.slice(0, 8)}.${kind}`;
      await downloadAuthenticated(url, filename);
      toast.success(`${kind.toUpperCase()} indiriliyor.`);
    } catch (e) {
      toast.error(e?.message || 'Dışa aktarma başarısız');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="campaign-detail-dialog">
        <DialogHeader>
          <DialogTitle>{campaign.name}</DialogTitle>
          <DialogDescription>
            {campaign.campaign_type} ·{' '}
            {detail?.stats
              ? `${detail.stats.total_codes} kod / ${detail.stats.active_codes} aktif / ${detail.stats.total_redemptions} kullanım`
              : '…'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleToggleActive}
              disabled={busy}
              data-testid="campaign-toggle-active-btn"
            >
              {detail?.is_active ? <PowerOff className="h-4 w-4 mr-1" /> : <Power className="h-4 w-4 mr-1" />}
              {detail?.is_active ? 'Devre dışı bırak' : 'Etkinleştir'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={exporting}
              data-testid="campaign-export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              data-testid="campaign-export-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Kod Üret (tek veya toplu)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Adet</Label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  data-testid="generate-count-input"
                />
              </div>
              <div>
                <Label className="text-xs">Tip</Label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="generate-type-select"
                >
                  <option value="fixed_credits">fixed_credits</option>
                  <option value="free_4k_generation">free_4k_generation</option>
                  <option value="feature_specific_usage">feature_specific_usage</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Value</Label>
                <Input
                  type="number"
                  min={1}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="generate-value-input"
                />
              </div>
              {type === 'feature_specific_usage' && (
                <div className="col-span-2 sm:col-span-3">
                  <Label className="text-xs">feature_key</Label>
                  <Input
                    value={featureKey}
                    onChange={(e) => setFeatureKey(e.target.value)}
                    data-testid="generate-feature-key-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs">user_type_allowed</Label>
                <select
                  value={userTypeAllowed}
                  onChange={(e) => setUserTypeAllowed(e.target.value)}
                  className="h-9 w-full rounded-md border border-slate-200 px-3 text-sm bg-white"
                  data-testid="generate-user-type-select"
                >
                  <option value="both">both</option>
                  <option value="doctor">doctor</option>
                  <option value="mom">mom</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">max_redemptions</Label>
                <Input
                  type="number"
                  min={1}
                  value={maxRedemptions}
                  onChange={(e) => setMaxRedemptions(e.target.value)}
                  data-testid="generate-max-redemptions-input"
                />
              </div>
              <div>
                <Label className="text-xs">Prefix (ops.)</Label>
                <Input
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                  placeholder="NEOB"
                  maxLength={8}
                  data-testid="generate-prefix-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-3 flex justify-end pt-2">
                <Button
                  onClick={handleGenerate}
                  disabled={busy}
                  data-testid="generate-codes-submit-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Üret'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Kodlar ({codes.length})</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kod</TableHead>
                    <TableHead>Tip</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>İzin</TableHead>
                    <TableHead>Kullanım</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((c) => (
                    <TableRow key={c.id} data-testid={`promo-code-row-${c.id}`}>
                      <TableCell className="font-mono text-xs">{c.code}</TableCell>
                      <TableCell>{c.type}</TableCell>
                      <TableCell>{c.value}</TableCell>
                      <TableCell>{c.user_type_allowed}</TableCell>
                      <TableCell>
                        {c.redeemed_count}/{c.max_redemptions}
                      </TableCell>
                      <TableCell>
                        <Badge variant={c.is_active ? 'default' : 'outline'}>
                          {c.is_active ? 'aktif' : 'pasif'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {c.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDisableCode(c.id)}
                            data-testid={`disable-code-btn-${c.id}`}
                          >
                            Devre dışı
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Buy Requests (PHASE 3.A — manual approval)
// ─────────────────────────────────────────────────────────────────────────────

function BuyRequestsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // request being rejected
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      const res = await adminCreditsApi.listBuyRequests(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFulfill = async (req) => {
    setBusyId(req.id);
    try {
      const res = await adminCreditsApi.fulfillBuyRequest(req.id, {});
      if (res.success) {
        toast.success(`${res.credits_granted} kredi tanımlandı.`);
      } else {
        toast.warning(`Talep zaten "${(res.reason || '').replace('already_', '')}" durumunda.`);
      }
      load();
    } catch (e) {
      toast.error(e?.message || 'İşlem başarısız');
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (rejectReason.trim().length < 2) {
      toast.error('Red sebebi en az 2 karakter olmalı.');
      return;
    }
    setBusyId(rejecting.id);
    try {
      const res = await adminCreditsApi.rejectBuyRequest(rejecting.id, rejectReason.trim());
      if (res.success) {
        toast.success('Talep reddedildi.');
      } else {
        toast.warning(`Talep zaten "${(res.reason || '').replace('already_', '')}" durumunda.`);
      }
      setRejecting(null);
      setRejectReason('');
      load();
    } catch (e) {
      toast.error(e?.message || 'İşlem başarısız');
    } finally {
      setBusyId(null);
    }
  };

  const STATUS_BADGE = {
    pending: { label: 'Bekliyor', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    paid: { label: 'Onaylandı', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    cancelled: { label: 'Reddedildi', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
    refunded: { label: 'İade', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  };

  return (
    <div className="space-y-3" data-testid="buy-requests-tab">
      <Card>
        <CardContent className="py-3 flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs">Durum</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="buy-requests-status-filter"
            >
              <option value="pending">Bekleyenler</option>
              <option value="paid">Onaylanmış</option>
              <option value="cancelled">Reddedilmiş</option>
              <option value="">Tümü</option>
            </select>
          </div>
          <Button onClick={load} disabled={loading} variant="outline" data-testid="buy-requests-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <div className="flex-1" />
          <div className="text-sm text-slate-500">
            Toplam: <span className="font-semibold tabular-nums">{total}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead className="text-right">Kredi</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead>İletişim</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-6">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Yükleniyor…
                  </TableCell>
                </TableRow>
              )}
              {!loading && !items.length && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-6">
                    {statusFilter === 'pending' ? 'Bekleyen talep yok.' : 'Sonuç yok.'}
                  </TableCell>
                </TableRow>
              )}
              {items.map((req) => {
                const badge = STATUS_BADGE[req.status] || { label: req.status, cls: '' };
                return (
                  <TableRow key={req.id} data-testid={`buy-request-row-${req.id}`}>
                    <TableCell className="text-xs text-slate-500">
                      {(req.created_at || '').split('T')[0]}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{req.user_email || '—'}</div>
                      <div className="text-xs text-slate-500 capitalize">
                        {req.user_name} · {req.user_type}
                      </div>
                    </TableCell>
                    <TableCell>{req.package_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{req.credit_amount}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {req.price} {req.currency}
                    </TableCell>
                    <TableCell className="capitalize">{req.contact_method}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={badge.cls}>
                        {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {req.status === 'pending' ? (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            disabled={busyId === req.id}
                            onClick={() => handleFulfill(req)}
                            data-testid={`buy-request-fulfill-btn-${req.id}`}
                          >
                            {busyId === req.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                Onayla
                              </span>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === req.id}
                            onClick={() => {
                              setRejecting(req);
                              setRejectReason('');
                            }}
                            data-testid={`buy-request-reject-btn-${req.id}`}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            Reddet
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent data-testid="buy-request-reject-dialog">
          <DialogHeader>
            <DialogTitle>Talebi Reddet</DialogTitle>
            <DialogDescription>
              {rejecting?.user_email} — {rejecting?.package_name} ({rejecting?.credit_amount} kredi)
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Red sebebi (zorunlu)</Label>
            <Input
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="örn. Ödeme alınmadı"
              data-testid="buy-request-reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejecting(null)}>
              İptal
            </Button>
            <Button
              variant="outline"
              onClick={submitReject}
              disabled={busyId === rejecting?.id}
              data-testid="buy-request-reject-submit-btn"
            >
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function SuperAdminCreditsPage() {
  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="super-admin-credits-page">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Coins className="h-6 w-6 text-emerald-600" />
            Kredi Yönetimi
          </h1>
          <p className="text-sm text-slate-500">
            Sistem geneli kredi durumu, kullanıcı yönetimi, fiyatlandırma ve promosyonlar.
          </p>
        </div>

        <Tabs defaultValue="overview">
          <TabsList data-testid="super-admin-credits-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <AlertCircle className="h-3.5 w-3.5 mr-1" /> Genel Bakış
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <UsersIcon className="h-3.5 w-3.5 mr-1" /> Kullanıcılar
            </TabsTrigger>
            <TabsTrigger value="buy-requests" data-testid="tab-buy-requests">
              <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Talepler
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">
              <Banknote className="h-3.5 w-3.5 mr-1" /> Fiyatlandırma
            </TabsTrigger>
            <TabsTrigger value="features" data-testid="tab-features">
              <Tags className="h-3.5 w-3.5 mr-1" /> Özellik Maliyeti
            </TabsTrigger>
            <TabsTrigger value="promo" data-testid="tab-promo">
              <Gift className="h-3.5 w-3.5 mr-1" /> Promosyon
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="buy-requests"><BuyRequestsTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="features"><FeatureCostsTab /></TabsContent>
          <TabsContent value="promo"><PromoTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
